/**
 * A hub address as a person types one (plan
 * `docs/plans/LOCAL-HTTP-AND-CLOUD-ADOPTION.md` §2/C4, card `t_16b7704a`).
 *
 * The app asks for an address in two places, and they must accept and reject
 * exactly the same strings:
 *
 *   - `components/server/ManualConnectModal.vue` — the Add-server dialog: a host
 *     field and a port field, validated before anything is dialled;
 *   - the per-server settings sheet (`components/server/ServerRouteSettings.vue`)
 *     — **one** field: the host, then an optional `:port`, the way an address is
 *     pasted out of a browser or a terminal.
 *
 * The rule that was in the dialog moves here rather than being copied: an IPv4
 * literal (four dotted parts) or a hostname (letters, digits and `-` in labels,
 * `.local` included — a hub announces itself as `Torik-0cca3964.local`).
 *
 * ── The port, and why its absence is not "unset" ───────────────────────────
 *
 * A typed address without a port means the hub's **front door**
 * (`HUB_FRONT_DOOR_PORT`, card `t_c2446418`): the port-less spelling is a URL
 * convention for 80, and the transport ladder behind any dial
 * (`hubApiAttempts`) still reaches an un-upgraded hub on `443`/`16161` from
 * there. So `parseHubAddressInput('192.168.1.20')` is `{ host, port: 80 }` — a
 * measured address, never a null one.
 */
import { HUB_FRONT_DOOR_PORT } from '../config/runtime';

/** An address a dial can be built from. */
export interface HubAddressInput {
  /** The host as it was typed: an IP literal or a name (`wasp.local`). */
  host: string;
  /** The port to dial — the front door when the text spelled none. */
  port: number;
}

/** Four dotted parts. The octet range is not checked — a dial is the check. */
const IPV4_LITERAL = /^\d{1,3}(\.\d{1,3}){3}$/;

/** A hostname: labels of letters/digits/`-`, joined by dots (`wasp.local`). */
const HOSTNAME = /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

/**
 * Is this string a host a hub could answer at? The dialog's own rule, one
 * function — an IPv4 literal or a hostname, and nothing else: a scheme, a path
 * and a port are stripped by `parseHubAddressInput` before this is asked.
 */
export function isValidHubHost(value: string): boolean {
  const host = value.trim();
  if (!host) return false;
  if (IPV4_LITERAL.test(host)) return true;
  return HOSTNAME.test(host);
}

/**
 * What the text means, or `null` when it is not an address at all.
 *
 * Accepts `host`, `host:port` and the same two behind an optional `http://` /
 * `https://` (a pasted address usually carries one) — the scheme is **dropped**:
 * which rung answers is the ladder's measurement, never a hint from a text
 * field. A trailing `/` and surrounding whitespace are ignored.
 */
export function parseHubAddressInput(
  value: string | null | undefined,
  fallbackPort: number = HUB_FRONT_DOOR_PORT,
): HubAddressInput | null {
  if (typeof value !== 'string') return null;
  let text = value.trim();
  if (!text) return null;
  text = text.replace(/^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//, '');
  text = text.replace(/\/+$/, '');
  if (!text) return null;

  // The port is what follows the colon a person writes by hand (`hub:16161`).
  // Bracketed IPv6 literals are not accepted here on purpose: the validator
  // below takes IPv4 literals and names only, exactly as the Add-server dialog
  // always has, and an address the dial path would have to re-spell is not an
  // address this field can promise.
  let host = text;
  let portText = '';
  const parts = text.split(':');
  if (parts.length === 2) {
    host = parts[0];
    portText = parts[1];
  }

  host = host.trim();
  if (!isValidHubHost(host)) return null;

  if (!portText) return { host, port: normalizeHubPort(fallbackPort) };
  if (!/^\d{1,5}$/.test(portText)) return null;
  const port = Number(portText);
  if (!Number.isFinite(port) || port <= 0 || port > 65535) return null;
  return { host, port };
}

/** A port a dial can carry: a whole number, or the front door. */
export function normalizeHubPort(value: unknown, fallback: number = HUB_FRONT_DOOR_PORT): number {
  const parsed = typeof value === 'string' ? Number(value) : value;
  if (typeof parsed !== 'number' || !Number.isFinite(parsed) || parsed <= 0 || parsed > 65535) {
    return fallback;
  }
  return Math.trunc(parsed);
}

/** `host`, or `host:port` when the port is not the front door. */
export function formatHubAddress(address: HubAddressInput): string {
  return address.port === HUB_FRONT_DOOR_PORT ? address.host : `${address.host}:${address.port}`;
}
